import React from 'react';

// services
import {CreditBookApi} from '../../shared/services/ApiService';

// helpers
import {useQuery} from '@tanstack/react-query';
import {useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {useTheme} from '@emotion/react';

// components
import {
  CardEmptyState,
  CardError,
  CardDetailsSubscription,
  PageTemplate,
  CardDetailsSkeleton,
  CardTableSkeleton,
} from '../../shared/components';
import {IconNoPayments} from './SubscriptionDetailsPage-styles';
import {SectionPayments} from './sections';

// types
import {
  CreditBookApiError,
  SubscriptionDetails,
  SubscriptionPayment,
} from '../../shared/services/ApiService/types';

/**
 * Fetch the subscription details
 * @param subscriptionId
 */
const fetchSubscriptionDetails = async (subscriptionId: string | undefined) => {
  const response = await CreditBookApi.subscription.getSubscriptionDetails(
    Number(subscriptionId),
  );
  return response.data;
};

/**
 * Fetch the subscription payments
 * @param subscriptionId
 */
const fetchSubscriptionPayments = async (
  subscriptionId: string | undefined,
) => {
  const response = await CreditBookApi.subscription.getSubscriptionPayment(
    Number(subscriptionId),
  );
  return response.data;
};

const SubscriptionDetailsPage = () => {
  const {t} = useTranslation();
  const {subscriptionId} = useParams();
  const {skeleton} = useTheme();

  // Fetch the subscription details
  const {
    data: subscriptionDetails,
    isLoading: subscriptionDetailsLoading,
    error: subscriptionDetailsError,
    isRefetching: isSubscriptionDetailsRefetching,
  } = useQuery<SubscriptionDetails, CreditBookApiError>({
    queryKey: ['getSubscriptionDetails'],
    queryFn: () => fetchSubscriptionDetails(subscriptionId),
  });

  // Fetch the subscription payments
  const {
    data: payments,
    isLoading: paymentsLoading,
    error: paymentsError,
    isRefetching: isPaymentsRefetching,
  } = useQuery<SubscriptionPayment[], CreditBookApiError>({
    queryKey: ['getSubscriptionPayments'],
    queryFn: () => fetchSubscriptionPayments(subscriptionId),
    initialData: [],
  });

  /**
   * Render the subscription details
   */
  const renderSubscriptionDetails = () => {
    if (subscriptionDetailsLoading || isSubscriptionDetailsRefetching) {
      return <CardDetailsSkeleton variant="detailed" />;
    }

    if (subscriptionDetailsError || !subscriptionDetails) {
      return (
        <CardError
          message={subscriptionDetailsError?.response?.data?.errorMessage}
        />
      );
    }

    return (
      <CardDetailsSubscription
        variant="detailed"
        subscription={subscriptionDetails}
      />
    );
  };

  /**
   * Render the payments
   */
  const renderPayments = () => {
    if (paymentsLoading || isPaymentsRefetching) {
      return (
        <CardTableSkeleton
          columns={[
            {width: skeleton.text.width.sm, empty: false},
            {width: skeleton.text.width.lg, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {width: skeleton.text.width.md, empty: false},
            {empty: true},
          ]}
          rowCount={3}
        />
      );
    }

    if (paymentsError) {
      return (
        <CardError
          title={t('subscription_payments')}
          message={paymentsError?.response?.data?.errorMessage}
        />
      );
    }

    if (payments.length === 0) {
      return (
        <CardEmptyState
          title={t('subscription_payments')}
          text={t('subscription_paymentEmptyState')}
          icon={IconNoPayments}
        />
      );
    }

    return (
      <SectionPayments
        payments={payments}
        idSubscription={Number(subscriptionId)}
      />
    );
  };

  const title = subscriptionDetails?.subscriptionName
    ? `${t('global_subscription')} ${subscriptionDetails?.subscriptionName}`
    : t('global_subscription');

  return (
    <PageTemplate title={title}>
      {renderSubscriptionDetails()}
      {renderPayments()}
    </PageTemplate>
  );
};

export default SubscriptionDetailsPage;
